'use client';

import { useEffect, useMemo, useRef } from 'react';

import { useQuery, useLazyQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { EventCard } from '@scorenco/components';
import { Types } from '@scorenco/core';
import { endOfDay, endOfWeek, startOfDay } from 'date-fns';
import { animate } from 'framer-motion';

import { getNextDay } from '../../utils';

import {
  GetIncomingEventsThisWeekDocument,
  GetIncomingEventsAllTimeDocument,
  GetEventsOfOneDayDocument,
} from './IncomingEvents.query.generated';

const getJour = (day: number) => {
  switch (day) {
    case 1:
      return 'lundi';
    case 2:
      return 'mardi';
    case 3:
      return 'mercredi';
    case 4:
      return 'jeudi';
    case 5:
      return 'vendredi';
    case 6:
      return 'samedi';
    case 7:
      return 'dimanche';
    default:
      return '';
  }
};

type IncomingEventsType = {
  clubHouse: Types.Clubhouse_Club_House;
  screen: Types.Clubhouse_Screen;
};

const style = {
  root: css`
    position: relative;
    height: 100vh;
    padding-top: 4rem;

    .incoming-events {
      width: 80%;
      margin: 1rem auto;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      overflow: hidden;
      margin-top: 4rem;
    }
  `,
};

export const IncomingEvents = ({ clubHouse, screen }: IncomingEventsType) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const clubId = clubHouse.club?.id;
  const screenDay = typeof screen.day === 'number' ? screen.day : undefined;
  const screenDuration =
    typeof screen.duration === 'number' ? screen.duration : undefined;

  const sundayDate = useMemo(() => {
    return endOfWeek(new Date(), {
      weekStartsOn: 1,
    });
  }, []);

  const getIncomingEventsThisWeekQuery = useQuery(
    GetIncomingEventsThisWeekDocument,
    {
      variables: {
        clubId: clubId ?? 0,
        sundayDate,
      },
      skip: Boolean(screenDay) || !clubId,
      context: {
        role: 'anonymous',
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [loadIncomingEventsAllTime, getIncomingEventsAllTimeQuery] =
    useLazyQuery(GetIncomingEventsAllTimeDocument, {
      fetchPolicy: 'cache-and-network',
    });

  const [loadEventsOfOneDay, getEventsOfOneDayQuery] = useLazyQuery(
    GetEventsOfOneDayDocument,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  useEffect(() => {
    const aggregateCount =
      getIncomingEventsThisWeekQuery.data
        ?.competitions_event_detail_by_club_id_aggregate?.aggregate?.count;

    if (aggregateCount === 0 && clubId) {
      loadIncomingEventsAllTime({
        variables: { clubId },
        context: { role: 'anonymous' },
      });
    }
  }, [
    clubId,
    getIncomingEventsThisWeekQuery.data
      ?.competitions_event_detail_by_club_id_aggregate?.aggregate?.count,
    loadIncomingEventsAllTime,
  ]);

  useEffect(() => {
    if (screenDay && clubId) {
      loadEventsOfOneDay({
        variables: {
          clubId,
          dateStart: startOfDay(getNextDay(screenDay)),
          dateEnd: endOfDay(getNextDay(screenDay)),
        },
        context: { role: 'anonymous' },
      });
    }
  }, [clubId, loadEventsOfOneDay, screenDay]);

  const dayEvents =
    getEventsOfOneDayQuery.data
      ?.competitions_event_detail_by_club_id_between_dates ?? [];
  const weeklyEvents =
    getIncomingEventsThisWeekQuery.data?.competitions_event_detail_by_club_id ??
    [];
  const allTimeEvents =
    getIncomingEventsAllTimeQuery.data?.competitions_event_detail_by_club_id ??
    [];

  const events = screenDay
    ? dayEvents
    : weeklyEvents.length > 0
      ? weeklyEvents
      : (getIncomingEventsAllTimeQuery.data
          ?.competitions_event_detail_by_club_id ?? allTimeEvents);

  const title = useMemo(() => {
    return screenDay ? `Matchs de ${getJour(screenDay)}` : 'Matchs à venir';
  }, [screenDay]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !screenDuration) {
      return;
    }

    const pauseDuration = 1; // 2 secondes
    const scrollDuration = screenDuration / 2 - 2 * pauseDuration; // durée d’un aller (haut → bas) en s
    if (scrollDuration <= 0) {
      return;
    }
    let direction: 'down' | 'up' = 'down';
    let controls: ReturnType<typeof animate>;

    const loop = () => {
      const from = container.scrollTop;
      const to =
        direction === 'down'
          ? container.scrollHeight - container.clientHeight
          : 0;

      controls = animate(from, to, {
        duration: scrollDuration,
        ease: 'linear',
        onUpdate: (v) => (container.scrollTop = v),
        onComplete: () => {
          direction = direction === 'down' ? 'up' : 'down';
          setTimeout(loop, pauseDuration * 1000);
        },
      });
    };

    // Lancer au départ après une pause initiale
    const startTimeout = setTimeout(loop, pauseDuration * 1000);

    return () => {
      clearTimeout(startTimeout);
      controls?.stop();
    };
  }, [events, screenDuration]);

  return (
    <Box css={style.root}>
      <Typography variant="h1" gutterBottom sx={{ textAlign: 'center' }}>
        {title}
      </Typography>

      <Paper
        variant="bordered"
        className="incoming-events"
        sx={{ padding: 0, maxHeight: '52rem' }}
        ref={scrollRef}
      >
        <Box
          css={(theme) => css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1px;
            background-color: ${theme.vars.palette.divider};
            width: 100%;
          `}
        >
          {events.map((event, idx) => {
            return (
              <Box
                key={idx}
                css={(theme) => css`
                  padding: 1.5rem 3rem;
                  background-color: ${idx % 4 === 3 || idx % 4 === 0
                    ? theme.vars.palette.background.body
                    : theme.vars.palette.background.paper};
                `}
              >
                <EventCard
                  key={idx}
                  event={event as Types.Competitions_Event_Detail}
                  clubId={clubId}
                  showShortName={true}
                />
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};
